export const SUB_USER_ROLES = [
  {
    key: "analyst",
    label: "Analyst",
    description: "View dashboards & reports",
    badge: "ANALYST",
  },
  {
    key: "manager",
    label: "Manager",
    description: "Edit data & invite users",
    badge: "MANAGER",
  },
  {
    key: "account_admin",
    label: "Account Admin",
    description: "Full access incl. billing",
    badge: "ACCOUNT ADMIN",
  },
];

export const SUB_USER_MODULES = [
  { key: "dashboard", label: "Dashboard", icon: "ki-element-11" },
  { key: "products", label: "Products", icon: "ki-directbox-default" },
  { key: "alerts", label: "Alerts", icon: "ki-notification-on" },
  { key: "expenses", label: "Expenses", icon: "ki-finance-calculator" },
  { key: "ai_chat", label: "AI Chat", icon: "ki-abstract-26" },
  { key: "imports_n_exports", label: "Import & Export", icon: "ki-cloud-add" },
  { key: "advanced_settings", label: "Advanced Settings", icon: "ki-setting-2" },
  { key: "marketplace_credentials", label: "Marketplace Credentials", icon: "ki-shop" },
  { key: "billing", label: "Billing", icon: "ki-wallet" },
  { key: "child_sellers", label: "Seller Accounts", icon: "ki-people" },
  { key: "sub_users", label: "Manage Users", icon: "ki-profile-user" },
];

export const ROLE_LABEL_MAP = Object.fromEntries(
  SUB_USER_ROLES.map((r) => [r.key, r.label])
);

export const MODULE_LABEL_MAP = Object.fromEntries(
  SUB_USER_MODULES.map((m) => [m.key, m.label])
);

export const FALLBACK_ROLE_DEFAULT_PAGES = {
  analyst: ["dashboard", "alerts"],
  manager: [
    "dashboard",
    "alerts",
    "ai_chat",
    "products",
    "expenses",
    "expense_categories",
    "imports_n_exports",
  ],
  account_admin: [
    "dashboard",
    "products",
    "alerts",
    "expenses",
    "expense_categories",
    "ai_chat",
    "imports_n_exports",
    "my_profile",
    "advanced_settings",
    "marketplace_credentials",
  ],
};

export const FALLBACK_PAGES = [
  { key: "dashboard", label: "Dashboard" },
  { key: "ai_chat", label: "AI Copilot" },
  { key: "alerts", label: "Alerts" },
  { key: "products", label: "Products" },
  {
    key: "expenses",
    label: "Expenses",
  },
  { key: "imports_n_exports", label: "Import & Export" },
  {
    key: "configurations",
    label: "Configurations",
    children: [
      { key: "my_profile", label: "My Profile" },
      { key: "marketplace_credentials", label: "Marketplace Credentials" },
      { key: "advanced_settings", label: "Advanced Settings" },
    ],
  },
];

export const MODULE_ICON_MAP = {
  dashboard: "ki-element-11",
  products: "ki-directbox-default",
  alerts: "ki-notification-on",
  expenses: "ki-finance-calculator",
  expense_categories: "ki-category",
  ai_chat: "ki-abstract-26",
  imports_n_exports: "ki-cloud-add",
  data_logs: "ki-cloud-add",
  configurations: "ki-setting-2",
  my_profile: "ki-user",
  marketplace_credentials: "ki-shop",
  user_settings: "ki-shop",
  advanced_settings: "ki-setting-2",
  billing: "ki-wallet",
  child_sellers: "ki-people",
  sub_users: "ki-profile-user",
  orders: "ki-basket",
};

export function enrichPagesWithIcons(pages = []) {
  return pages.map((page) => ({
    ...page,
    icon: MODULE_ICON_MAP[page.key] || "ki-element-11",
    children: page.children?.map((child) => ({
      ...child,
      icon: MODULE_ICON_MAP[child.key] || "ki-element-11",
    })),
  }));
}

export function collectPageKeys(pages = []) {
  const keys = [];
  pages.forEach((page) => {
    keys.push(page.key);
    page.children?.forEach((child) => keys.push(child.key));
  });
  return keys;
}

export function buildModuleLabelMap(pages = []) {
  const map = { ...MODULE_LABEL_MAP };
  pages.forEach((page) => {
    map[page.key] = page.label;
    page.children?.forEach((child) => {
      map[child.key] = child.label;
    });
  });
  return map;
}

export function collectAssignablePermissionKeys(roleDefaultPages = {}) {
  const keys = new Set();
  Object.values(roleDefaultPages).forEach((permissions) => {
    permissions?.forEach((key) => keys.add(key));
  });
  return keys;
}

export function findPageNode(pages = [], key) {
  for (const page of pages) {
    if (page.key === key) return { page, isChild: false };
    const child = page.children?.find((item) => item.key === key);
    if (child) return { page, child, isChild: true };
  }
  return null;
}

export function getChildKeys(page) {
  return (page?.children || []).map((child) => child.key);
}

export function applyModuleToggle(pages, selectedModules, moduleKey, assignableKeys) {
  const moduleSet = new Set(selectedModules);
  const node = findPageNode(pages, moduleKey);

  if (!node) {
    if (moduleSet.has(moduleKey)) moduleSet.delete(moduleKey);
    else moduleSet.add(moduleKey);
    return [...moduleSet];
  }

  const { page, isChild } = node;
  const childKeys = getChildKeys(page);
  const hasChildren = childKeys.length > 0;

  if (!isChild && hasChildren) {
    const parentSelectable = isPageSelectable(page, assignableKeys);

    if (parentSelectable) {
      const turningOn = !moduleSet.has(moduleKey);
      if (turningOn) {
        moduleSet.add(moduleKey);
        childKeys.forEach((key) => moduleSet.add(key));
      } else {
        moduleSet.delete(moduleKey);
        childKeys.forEach((key) => moduleSet.delete(key));
      }
    } else {
      const allSelected = childKeys.every((key) => moduleSet.has(key));
      if (allSelected) childKeys.forEach((key) => moduleSet.delete(key));
      else childKeys.forEach((key) => moduleSet.add(key));
    }

    return [...moduleSet];
  }

  if (moduleSet.has(moduleKey)) moduleSet.delete(moduleKey);
  else moduleSet.add(moduleKey);

  if (isChild && hasChildren && isPageSelectable(page, assignableKeys)) {
    const allChildrenSelected = childKeys.every((key) => moduleSet.has(key));
    if (allChildrenSelected) moduleSet.add(page.key);
    else moduleSet.delete(page.key);
  }

  return [...moduleSet];
}

export function getParentToggleState(page, selectedModules = [], assignableKeys) {
  const childKeys = getChildKeys(page);
  if (!childKeys.length) {
    return {
      checked: selectedModules.includes(page.key),
      indeterminate: false,
    };
  }

  const parentSelectable = isPageSelectable(page, assignableKeys);
  const selectedChildCount = childKeys.filter((key) => selectedModules.includes(key)).length;

  if (parentSelectable) {
    const checked = selectedModules.includes(page.key);
    return {
      checked,
      indeterminate: !checked && selectedChildCount > 0 && selectedChildCount < childKeys.length,
    };
  }

  return {
    checked: selectedChildCount === childKeys.length,
    indeterminate: selectedChildCount > 0 && selectedChildCount < childKeys.length,
  };
}

export function isPageSelectable(page, assignableKeys) {
  if (!page?.children?.length) return true;
  return assignableKeys.has(page.key);
}

export function getSubUserApiErrorMessage(response) {
  const fieldErrors = response?.data;
  if (fieldErrors && typeof fieldErrors === "object" && !Array.isArray(fieldErrors)) {
    const messages = Object.values(fieldErrors).flat().filter(Boolean).map(String);
    if (messages.length) return messages.join(" ");
  }

  if (Array.isArray(response?.error) && response.error.length) {
    return response.error.map(String).join(" ");
  }

  return response?.message || "Request failed. Please try again.";
}

export function mapSellerAccessToAccountAccess(sellerAccess = []) {
  return sellerAccess.map((entry) => ({
    account_id: entry.seller_user_id,
    account_name: entry.seller_name || entry.name || `Account ${entry.seller_user_id}`,
    marketplace: entry.marketplace || "",
    category: entry.category || "",
    enabled: !!entry.enabled,
    modules: entry.page_permissions || [],
  }));
}

export function buildDefaultAccountAccess(accounts = []) {
  return accounts.map((acc) => ({
    account_id: acc.id,
    account_name: acc.name || acc.seller_name || acc.email || `Account ${acc.id}`,
    marketplace: acc.marketplace || "",
    category: acc.category || acc.seller_category || "",
    enabled: false,
    expanded: false,
    modules: [],
  }));
}

export const SUB_USER_STATUS_LABELS = {
  active: "Active",
  pending: "Pending",
  disabled: "Disabled",
  invite_sent: "Pending",
};

export function normalizeSubUserRecord(record) {
  const roleKey = record?.sub_user_role || record?.role || "analyst";
  const rawStatus = record?.sub_user_status || record?.status || "active";
  const status = rawStatus === "invite_sent" ? "pending" : rawStatus;

  return {
    id: record?.id,
    name: record?.name || "",
    email: record?.email || "",
    role: roleKey,
    status,
    sub_user_status: record?.sub_user_status,
    accounts_count:
      record?.accounts_count ??
      record?.seller_access?.filter((s) => s.enabled)?.length ??
      0,
    modules_count: record?.modules_count ?? 0,
    created_at: record?.created_at,
    seller_access: record?.seller_access || [],
  };
}
